import { DragBehavior, ScaleLinear, axisLeft, Selection, EnterElement, D3DragEvent, drag, select, format } from "d3"
import { D3DragOverlay } from "./D3DragOverlay"
import { ReactCallbacks } from "../../Types/ReactCallbacks"
import { D3OneToOneRenderable } from "./D3OneToOneRenderable"
import ReactDOM from "react-dom"
import { MdArrowDownward, MdArrowUpward } from "react-icons/md"
import { MobergFontFamily, MobergFontSize, MobergIconSize } from "../../../../../../Moberg"
import { getNiceTicks, getTickYCorrection } from "./axisUtils"
import { AXIS_TICK_FONT_SIZE } from "../Visualizations/Constants"


const ARROW_X_OFFSET = -55
const DOWN_ARROW_Y_CORRECTION = -10
const UP_ARROW_Y_CORRECTION = -3

export type DataOutOfViewArrow = {
	color: string
}

export type D3ValueAxisConfig = {
	graphId: string
	scale: ScaleLinear<any, any, any>
	label?: string
	upArrow?: DataOutOfViewArrow
	downArrow?: DataOutOfViewArrow
	onDrag?: () => void
	onDoubleClick?: () => void
	onDragEnd?: () => void
	onDragStart?: () => void
}

export type ArrowUpdate = {
	downArrow?: DataOutOfViewArrow
	upArrow?: DataOutOfViewArrow
}

export class D3ValueAxis extends D3OneToOneRenderable<SVGGElement, SVGGElement, D3ValueAxisConfig, ReactCallbacks<any>> {
	public isDragging: boolean = false

	private d3AxisClassName: string = "d3-axis-left"
	private labelClassName: string = "d3-value-axis-label"
	private upArrowClassName: string = "d3-data-out-of-view-up"
	private downArrowClassName: string = "d3-data-out-of-view-down"

	private dragOverlay?: D3DragOverlay
	private width: number = 30
	private dragBehavior: DragBehavior<any, any, any>
	private dragDistance: number = 0

	constructor(root: SVGGElement, config: D3ValueAxisConfig, reactCallbacks: ReactCallbacks<any>) {
		super(root, config, "d3-value-axis", reactCallbacks)

		this.dragBehavior = drag()
			.clickDistance(3)
			.on("start", this.onDragStart)
			.on("drag", this.onDrag)
			.on("end", this.onDragEnd)

		this.render()
	}

	public enter = (enterElements: Selection<EnterElement, any, any, any>): Selection<any, any, any, any> => {
		const axisGroup = enterElements
			.append("g")
			.attr("class", this.className)
			.style("font-family", MobergFontFamily.REGULAR)
				
		const upArrowContainer = axisGroup
			.append("g")
			.attr("class", this.upArrowClassName)

		this.createUpArrow(upArrowContainer)

		const downArrowContainer = axisGroup
			.append("g")
			.attr("transform", `translate(0, ${this.config.scale.range()[0]})`)
			.attr("class", this.downArrowClassName)

		this.createDownArrow(downArrowContainer)

		const d3AxisLeft = axisGroup.append("g")
			.attr("class", this.d3AxisClassName)
			.call(
				axisLeft(this.config.scale)
				.tickValues(getNiceTicks(this.config.scale))
				.tickFormat(format(".2~f")) // Up to X decimal place precision, but less if not needed.
			)
			.style("user-select", "none") // disables highlighting the ticks
			.style("font-size", AXIS_TICK_FONT_SIZE)
			.style("font-family", MobergFontFamily.REGULAR)

		
		this.updateTickStyles(d3AxisLeft)

		// create the label
        axisGroup.append("text")
            .attr("text-anchor", "middle")
            .attr("class", this.labelClassName)
            .style("font-family", MobergFontFamily.REGULAR)
            .style("font-size", MobergFontSize.REGULAR)
            .attr("transform", `translate(-40, ${this.config.scale.range()[0] / 2 }), rotate(-90)`)
            .text(() => this.config.label ?? "")
            .attr("fill", "var(--color-chart-axis-text)")

		axisGroup.each((config, i, nodes) => this.createChildren(config, i, nodes))

		return axisGroup
	}

	public update = (updateElements: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		const d3AxisLeft = updateElements.select("." + this.d3AxisClassName) as Selection<SVGGElement, any, any, any>
		d3AxisLeft.call(
				axisLeft(this.config.scale)
				.tickValues(getNiceTicks(this.config.scale))
				.tickFormat(format(".2~f")) // Up to X decimal place precision, but less if not needed.
			)
			.style("font-size", AXIS_TICK_FONT_SIZE)
			.style("font-family", MobergFontFamily.REGULAR)

		// update the label
        updateElements.select("." + this.labelClassName)
            .attr("transform", `translate(-40, ${this.config.scale.range()[0] / 2 }), rotate(-90)`)
            .text(() => this.config.label ?? "")

		this.renderArrows(updateElements)	
		this.renderChildren()
		this.updateTickStyles(d3AxisLeft)

		return updateElements
	}

	public updateArrows = ({ upArrow, downArrow }: ArrowUpdate) => {
		this.config.upArrow = upArrow
		this.config.downArrow = downArrow
		this.renderArrows()
	}

	private renderArrows = (root?: Selection<any, any, any, any>) => {
		const rootSelection = root ?? select(this.root) as Selection<SVGGElement, any, any, any>

		this.applyArrowContainerStyles(rootSelection.select("." + this.upArrowClassName), this.config.upArrow)
		this.applyArrowContainerStyles(rootSelection.select("." + this.downArrowClassName), this.config.downArrow)
			.attr("transform", `translate(0, ${this.config.scale.range()[0]})`)	

		this.updateTickStyles(rootSelection.select("." + this.d3AxisClassName))
	}

	private updateTickStyles = (axisSelection: Selection<any, any, any, any>) => {
		axisSelection
			.style("color", "var(--color-chart-axis-text)")
			.selectAll(".tick text")
				.attr("y", (d, i, nodes) => getTickYCorrection(i, nodes))
	}

	private applyArrowContainerStyles = (selection: Selection<any, any, any, any>, arrow?: DataOutOfViewArrow): Selection<any, any, any, any> => {
		return selection
			.style("opacity", arrow ? 1 : 0)
			.style("color", "var(--color-chart-arrow)")
	}

	private onDrag = (dragEvent: D3DragEvent<any, any, any>) => {
		this.dragDistance += Math.abs(dragEvent.dx) + Math.abs(dragEvent.dy);

		const min = this.config.scale.domain()[0]
		const max = this.config.scale.domain()[1]
		const height = this.config.scale.range()[0] - this.config.scale.range()[1]

		const range = max - min
		const sensitivity = range / height
		let { dx, dy } = dragEvent

		const pointerY = dragEvent.y + dy
		const zoomScaling = pointerY / height

		const newMinY = min + dy * sensitivity + 0.5 * (dx * sensitivity) * (1 - zoomScaling)
		const newMaxY = max + dy * sensitivity - 0.5 * (dx * sensitivity) * zoomScaling

		this.config.scale.domain([newMinY, newMaxY])

		if (this.config.onDrag) {
			this.config.onDrag()
		}

		requestAnimationFrame(() => {
			this.render()
		})
	}

	private onDragEnd = () => {
		this.isDragging = false

		if (this.dragDistance < 5) {
			return // No drag, this prevents clicks from firing the drag end handler.
		}

		if (this.config.onDragEnd) {
			this.config.onDragEnd()
		}
	}

	private onDragStart = () => {
		this.isDragging = true
		this.dragDistance = 0

		if (this.config.onDragStart) {
			this.config.onDragStart()
		}
	}

	private createUpArrow(container: Selection<any, any, any, any>) {
		this.applyArrowContainerStyles(container, this.config.upArrow)
		ReactDOM.render(MdArrowUpward({ 
			size: MobergIconSize.X_SMALL, 
			style: { transform: `translate(${ARROW_X_OFFSET}px, ${UP_ARROW_Y_CORRECTION}px)`}
		}), container.node())
	}

	private createDownArrow(container: Selection<any, any, any, any>) {
		this.applyArrowContainerStyles(container, this.config.downArrow)
		ReactDOM.render(MdArrowDownward({ 
			size: MobergIconSize.X_SMALL, 
			style: { transform: `translate(${ARROW_X_OFFSET}px, ${DOWN_ARROW_Y_CORRECTION}px)` }
		}), container.node())
	}

	protected updateDerivedState(): void {
		this.updateChildren()
	}

	protected createChildren(config: D3ValueAxisConfig, index: number, nodes: ArrayLike<SVGGElement>): void {
		const root = nodes[index]
		const height = config.scale.range()[0]
		const boundingBox = { x: -this.width, y: 0, width: this.width, height }
		this.dragOverlay = new D3DragOverlay(root, { boundingBox, dragBehavior: this.dragBehavior }, this.reactCallbacks, { onDoubleClick: this.config?.onDoubleClick })
	}

	protected updateChildren = () => {
		const height = this.config.scale.range()[0]
		const boundingBox = { x: -this.width, y: 0, width: this.width, height }
		this.dragOverlay?.updateConfig({boundingBox, dragBehavior: this.dragBehavior })
	}

	protected renderChildren = () => {
		this.dragOverlay?.render()
	}
}
